import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import {
  RemoteParticipantWrapper as BaseRemoteParticipant,
  RemoteParticipantWrapperProps,
} from "../session";
import { isWhiteboardModeAtom } from "../session/messages/state";
import { useSendWhiteboardSnapshotCallback } from "../session/messages/useSendWhiteboardSnapshotCallback";
import { UserType } from "../types";
import {
  remoteDataDelegatorsAtom,
  type RemoteDataDelegator,
  type RemoteMediaDelegator,
  type RemoteParticipantMediaEventHandler,
} from "../video-sdk";
import { useHandleReceivedMessageCallback } from "./messages/useHandleReceivedMessageCallback";
import { useSendProviderParticipantStateCallback } from "./messages/useSendProviderParticipantStateCallback";
import { providerShortIdAtom } from "./state";

function checkShouldSendWhiteboardSnapshot(
  storedRemoteDataDelegators: RemoteDataDelegator[],
  subscribedDataDelegator: RemoteMediaDelegator,
  isWhiteboardMode: boolean,
) {
  const isAlreadySubscribedToDataTrack = storedRemoteDataDelegators.some(
    (storedTrack) => storedTrack.sid === subscribedDataDelegator.sid,
  );
  return isWhiteboardMode && !isAlreadySubscribedToDataTrack;
}

export function RemoteParticipant(
  props: Omit<RemoteParticipantWrapperProps, "onMessageReceived">,
) {
  const handleReceivedMessage = useHandleReceivedMessageCallback();
  const getIsWhiteboardMode = useAtomCallback(
    useCallback((get) => get(isWhiteboardModeAtom), []),
  );
  const sendWhiteboardSnapshot = useSendWhiteboardSnapshotCallback();
  const getProviderShortId = useAtomCallback(
    useCallback((get) => get(providerShortIdAtom), []),
  );
  const getRemoteDataDelegators = useAtomCallback(
    useCallback((get) => get(remoteDataDelegatorsAtom), []),
  );

  const sendParticipantState = useSendProviderParticipantStateCallback();

  const onDataTrackSubscribed: RemoteParticipantMediaEventHandler = useCallback(
    async (subscribedMedia) => {
      sendParticipantState();
      const shouldSendWhiteboardSnapshot = checkShouldSendWhiteboardSnapshot(
        getRemoteDataDelegators(),
        subscribedMedia,
        getIsWhiteboardMode().value,
      );
      if (shouldSendWhiteboardSnapshot) {
        await sendWhiteboardSnapshot({
          entityType: UserType.PROVIDER,
          entityId: getProviderShortId() ?? "",
        });
      }
    },
    [
      getIsWhiteboardMode,
      getProviderShortId,
      getRemoteDataDelegators,
      sendParticipantState,
      sendWhiteboardSnapshot,
    ],
  );

  return (
    <BaseRemoteParticipant
      {...props}
      onMessageReceived={handleReceivedMessage}
      onDataTrackSubscribed={onDataTrackSubscribed}
    />
  );
}
